import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useDfcThemeMode } from '../../../context/DfcThemeModeContext';
import { CLUB15_ART, CLUB15_COLOR, CLUB15_LOADER_LABEL } from '../../../Utils/club15Brand';

const breathe = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(0.98); }
  50% { opacity: 1; transform: scale(1); }
`;

const Screen = styled('div', {
  shouldForwardProp: prop => prop !== 'dark' && prop !== 'fullScreen',
})<{ dark: boolean; fullScreen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: ${({ fullScreen }) => (fullScreen ? '100vh' : '60vh')};
  /* The page's own background, so nothing changes colour around the logo. */
  background: ${({ dark }) => (dark ? CLUB15_COLOR.charcoal : CLUB15_COLOR.cream)};

  img {
    width: auto;
    height: 72px;
    animation: ${breathe} 1.6s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    img {
      animation: none;
    }
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

interface Club15PageLoaderProps {
  /** The whole viewport (before the company loads), or the page area (while a page's code loads) */
  fullScreen?: boolean;
}

/**
 * Club 15's loader: its logo, breathing, on the page's own background (charcoal logo on cream
 * in light mode, cream on charcoal in dark). Static under `prefers-reduced-motion`. Like ASA's
 * loader it needs nothing from the company record.
 */
const Club15PageLoader: React.FC<Club15PageLoaderProps> = ({ fullScreen = false }) => {
  const { mode } = useDfcThemeMode();
  const dark = mode === 'dark';
  return (
    <Screen
      dark={dark}
      fullScreen={fullScreen}
      role="status"
      aria-live="polite"
      data-testid="club15-page-loader"
    >
      <img src={dark ? CLUB15_ART.logoCream : CLUB15_ART.logoCharcoal} alt="" aria-hidden="true" />
      <VisuallyHidden>{CLUB15_LOADER_LABEL}</VisuallyHidden>
    </Screen>
  );
};

export default Club15PageLoader;
